import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchFaturamentoEcommerce, fetchFaturamentoLojaFisica } from '../../services/api';

/**
 * FaturamentoHeader — faturamento do período no header do catálogo, com o MESMO
 * período do ano passado ao lado para comparação.
 *
 * Abre no mês corrente (dia 1 → hoje). O usuário pode trocar o período; o card do
 * ano anterior acompanha automaticamente as datas escolhidas (mesmo dia/mês, ano -1),
 * que é o que torna a comparação honesta — comparar mês fechado com mês pela metade
 * daria uma queda falsa.
 */

const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** 'YYYY-MM-DD' -> 'DD/MM' (rótulo curto do intervalo) */
const curto = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

/** Data LOCAL em ISO. toISOString() daria UTC — em UTC-3 vira o dia seguinte após as 21h. */
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hojeIso = () => iso(new Date());
const primeiroDiaMes = () => {
  const d = new Date();
  return iso(new Date(d.getFullYear(), d.getMonth(), 1));
};

/**
 * @param {'ecommerce'|'centro'|'caxias'} loja  qual faturamento buscar (default: ecommerce)
 * @param {boolean} flat  visual flat/claro (sem gradiente) usado no Catálogo E-commerce.
 *   Default false preserva PIXEL A PIXEL o visual atual (gradiente roxo) usado em
 *   LojaCentro/LojaCaxias — não remover o default sem checar essas duas telas.
 */
export default function FaturamentoHeader({ loja = 'ecommerce', flat = false }) {
  const [inicio, setInicio] = useState(primeiroDiaMes);
  const [fim, setFim] = useState(hojeIso);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(false);

  const reqRef = useRef(0);
  const debounceRef = useRef(null);

  const carregar = useCallback(async (de, ate) => {
    const id = ++reqRef.current;
    setCarregando(true);
    setErro(false);
    try {
      const r = loja === 'ecommerce'
        ? await fetchFaturamentoEcommerce({ dataInicio: de, dataFim: ate })
        : await fetchFaturamentoLojaFisica({ loja, dataInicio: de, dataFim: ate });
      if (id !== reqRef.current) return;              // resposta velha — descarta
      if (r && r.success) setDados(r.data); else setErro(true);
    } catch (_) {
      if (id === reqRef.current) setErro(true);
    } finally {
      if (id === reqRef.current) setCarregando(false);
    }
  }, [loja]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => carregar(inicio, fim), 300);
    return () => clearTimeout(debounceRef.current);
  }, [inicio, fim, carregar]);

  const atual = dados?.atual;
  const anterior = dados?.anoAnterior;
  const variacao = dados?.variacao;
  const subiu = typeof variacao === 'number' && variacao >= 0;
  // Zero pedidos no ano passado = período não existe no histórico (ex.: Caxias antes de out/2025)
  const semBase = !!anterior && anterior.pedidos === 0 && anterior.total === 0;

  // Descontos e trocas só existem na loja física (o e-commerce não manda esses campos).
  const percentualDesconto = atual?.bruto > 0 ? (atual.descontos / atual.bruto) * 100 : null;
  const trocas = atual?.trocas;

  const atalho = (de, ate) => { setInicio(de); setFim(ate); setAberto(false); };
  const hoje = new Date();
  const atalhos = [
    { rot: 'Mês atual', de: primeiroDiaMes(), ate: hojeIso() },
    {
      rot: 'Mês passado',
      de: iso(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
      ate: iso(new Date(hoje.getFullYear(), hoje.getMonth(), 0)),
    },
    { rot: 'Últimos 30 dias', de: iso(new Date(Date.now() - 29 * 864e5)), ate: hojeIso() },
    { rot: 'Ano atual', de: `${hoje.getFullYear()}-01-01`, ate: hojeIso() },
  ];

  const anoAtual = Number((atual?.inicio || inicio).slice(0, 4));

  // ── Estilos condicionais (flat = Catálogo E-commerce; default = LojaCentro/LojaCaxias, pixel a pixel) ──
  const mainBtnStyle = flat
    ? {
        background: '#fff',
        boxShadow: aberto ? '0 0 0 3px rgba(26,37,48,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid #e8ecef', borderRadius: 8, padding: '8px 14px', minWidth: 210,
        cursor: 'pointer', textAlign: 'left', transition: 'box-shadow .15s',
      }
    : {
        background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
        boxShadow: aberto ? '0 0 0 3px rgba(79,70,229,0.35)' : '0 2px 8px rgba(79,70,229,0.3)',
        border: 'none', borderRadius: 8, padding: '8px 14px', minWidth: 210,
        cursor: 'pointer', textAlign: 'left', transition: 'box-shadow .15s',
      };
  const labelColor = flat ? '#7f8c8d' : '#fff';
  const valorColor = flat ? '#1a2530' : '#fff';
  const periodoBadgeStyle = flat
    ? { background: '#f5f7fa', color: '#1a2530', border: '1px solid #dde3e8' }
    : { background: 'rgba(255,255,255,0.18)', color: '#fff', border: 'none' };
  const descontosBorderTop = flat ? '1px solid #eef1f4' : '1px solid rgba(255,255,255,0.22)';
  const descontosTextColor = flat ? '#7f8c8d' : 'rgba(255,255,255,0.82)';
  const descontosStrongColor = flat ? '#1a2530' : '#fff';
  const coberturaBadgeStyle = flat
    ? { color: '#92400e', background: '#fef3c7' }
    : { color: '#fef3c7', background: 'rgba(251,191,36,0.25)' };

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, position: 'relative' }}>
      {/* ── Faturamento do período — CARD INTEIRO clicável (abre o seletor) ── */}
      <button
        onClick={() => setAberto(v => !v)}
        title="Clique para escolher o período do faturamento"
        style={mainBtnStyle}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '1.1rem' }}>📈</span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                color: labelColor, fontSize: '0.65rem', opacity: flat ? 1 : 0.9,
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}
            >
              Faturamento {curto(inicio)}–{curto(fim)}
            </div>
            <div style={{ fontWeight: 700, color: valorColor, fontSize: '1.1rem', lineHeight: 1.2 }}>
              {carregando ? '…' : erro ? '—' : brl(atual?.total)}
            </div>
          </div>
          {/* Afordância explícita: sem isso o usuário não descobre que dá pra trocar o período */}
          <span
            style={{
              display: 'flex', alignItems: 'center', gap: 3,
              ...periodoBadgeStyle,
              borderRadius: 6, padding: '3px 7px', fontSize: '0.66rem',
              fontWeight: 700, whiteSpace: 'nowrap',
            }}
          >
            📅 Período ▾
          </span>
        </div>

        {/* ── Descontos e trocas — só na loja física (bater o olho sem abrir o seletor) ── */}
        {!carregando && !erro && (typeof atual?.descontos === 'number' || trocas) && (
          <div
            style={{
              marginTop: 5, paddingTop: 5, borderTop: descontosBorderTop,
              display: 'flex', flexDirection: 'column', gap: 2,
            }}
          >
            {typeof atual?.descontos === 'number' && (
              <div style={{ fontSize: '0.66rem', color: descontosTextColor, whiteSpace: 'nowrap' }}>
                Descontos <strong style={{ color: descontosStrongColor }}>{brl(atual.descontos)}</strong>
                {percentualDesconto !== null && ` (${percentualDesconto.toFixed(1)}%)`}
              </div>
            )}
            {trocas && (
              <div style={{ fontSize: '0.66rem', color: descontosTextColor, whiteSpace: 'nowrap' }}>
                {trocas.disponivel === false ? (
                  <>Trocas <span>sem dado no período</span></>
                ) : (
                  <span title={`Valor de venda de referência dos itens trocados: ${brl(trocas.valorVendaReferencia)}`}>
                    Trocas <strong style={{ color: descontosStrongColor }}>{brl(trocas.valorTrocado)}</strong>
                    {typeof trocas.quantidade === 'number' && ` · ${trocas.quantidade.toLocaleString('pt-BR')} un.`}
                  </span>
                )}
              </div>
            )}
            {trocas && trocas.disponivel !== false && trocas.coberturaParcial && trocas.coberturaAte && (
              <div
                style={{
                  fontSize: '0.6rem', ...coberturaBadgeStyle,
                  borderRadius: 4, padding: '1px 5px', width: 'fit-content', marginTop: 1,
                }}
              >
                dados até {curto(trocas.coberturaAte)}
              </div>
            )}
          </div>
        )}
      </button>

      {/* ── Mesmo período do ano passado ── */}
      <div
        style={{
          background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 8, padding: '8px 14px', minWidth: 168,
        }}
        title={anterior ? `Mesmo intervalo em ${anterior.inicio?.slice(0, 4)}: ${curto(anterior.inicio)} a ${curto(anterior.fim)}` : 'Mesmo período do ano passado'}
      >
        <div style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Mesmo período {anoAtual - 1}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          {/* Sem NENHUMA venda no período do ano passado = não há histórico importado.
              Mostrar "R$ 0,00" faria parecer queda de 100%, que seria mentira. */}
          <span
            style={{ fontWeight: 700, color: semBase ? '#94a3b8' : '#475569', fontSize: semBase ? '0.86rem' : '1.1rem', lineHeight: 1.2 }}
            title={semBase ? 'Não há vendas desse período no histórico — sem base para comparar' : undefined}
          >
            {carregando ? '…' : erro ? '—' : semBase ? 'sem histórico' : brl(anterior?.total)}
          </span>
          {!carregando && !erro && typeof variacao === 'number' && (
            <span
              style={{
                fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap',
                color: subiu ? '#15803d' : '#dc2626',
                background: subiu ? '#dcfce7' : '#fee2e2',
                padding: '1px 6px', borderRadius: 999,
              }}
              title="Variação do período atual sobre o mesmo período do ano passado"
            >
              {subiu ? '▲' : '▼'} {Math.abs(variacao).toFixed(1)}%
            </span>
          )}
        </div>
      </div>

      {/* ── Seletor de período ── */}
      {aberto && (
        <>
          <div onClick={() => setAberto(false)} style={{ position: 'fixed', inset: 0, zIndex: 1200 }} />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1201,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
              boxShadow: '0 12px 32px rgba(15,23,42,0.18)', padding: 14, width: 290,
            }}
          >
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 8 }}>
              Período
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <label style={{ flex: 1, fontSize: '0.7rem', color: '#94a3b8' }}>
                De
                <input
                  type="date" value={inicio} max={fim}
                  onChange={(e) => e.target.value && setInicio(e.target.value)}
                  style={{ width: '100%', padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: '0.8rem', color: '#1e293b' }}
                />
              </label>
              <label style={{ flex: 1, fontSize: '0.7rem', color: '#94a3b8' }}>
                Até
                <input
                  type="date" value={fim} min={inicio}
                  onChange={(e) => e.target.value && setFim(e.target.value)}
                  style={{ width: '100%', padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: '0.8rem', color: '#1e293b' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {atalhos.map(a => (
                <button
                  key={a.rot}
                  onClick={() => atalho(a.de, a.ate)}
                  style={{
                    background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569',
                    borderRadius: 999, padding: '4px 10px', fontSize: '0.74rem',
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {a.rot}
                </button>
              ))}
            </div>
            {atual && !carregando && (
              <div style={{ marginTop: 10, paddingTop: 9, borderTop: '1px solid #f1f5f9', fontSize: '0.74rem', color: '#64748b' }}>
                <div>
                  {atual.pedidos.toLocaleString('pt-BR')} pedidos · {atual.itens.toLocaleString('pt-BR')} itens · ticket {brl(atual.ticketMedio)}
                </div>
                {/* Descontos e trocas agora aparecem direto no card de faturamento (sempre visível) */}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
